import React, { useState, useEffect } from 'react';
import { useToast } from '../ui/ToastProvider';
import api from '../../services/api';

interface ApiKey {
  keyId: string;
  name: string;
  permissions: string[];
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  lastUsedAt?: string;
  expiresAt?: string;
  allowedIPs?: string[];
  rateLimit: {
    requestsPerMinute: number;
    requestsPerHour: number;
    requestsPerDay: number;
  };
  status: 'active' | 'expired';
}

interface CreateApiKeyData {
  name: string;
  permissions: string[];
  expiresInDays?: number;
  allowedIPs?: string[];
  rateLimit: {
    requestsPerMinute: number;
    requestsPerHour: number;
    requestsPerDay: number;
  };
}

const ApiKeyManager: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState<CreateApiKeyData>({
    name: '',
    permissions: ['articles:read', 'articles:export'],
    expiresInDays: 365,
    allowedIPs: [],
    rateLimit: {
      requestsPerMinute: 100,
      requestsPerHour: 2000,
      requestsPerDay: 20000
    }
  });
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const { showSuccess, showError } = useToast();

  const availablePermissions = [
    { value: 'articles:read', label: 'Artikel lesen' },
    { value: 'articles:export', label: 'Artikel exportieren' },
    { value: 'barcodes:read', label: 'Barcodes lesen' },
    { value: 'units:read', label: 'Einheiten lesen' },
    { value: 'dropdown-options:read', label: 'Dropdown-Optionen lesen' },
    { value: 'suppliers:read', label: 'Lieferanten lesen' },
    { value: 'brands:read', label: 'Marken lesen' },
    { value: 'labels:read', label: 'Labels lesen' }
  ];

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api-keys');
      setApiKeys(response.data.data);
    } catch (error) {
      showError('Fehler beim Laden der API-Keys');
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api-keys', newKeyData);
      
      setCreatedApiKey(response.data.data.apiKey);
      setShowKeyModal(true);
      setShowCreateModal(false);
      fetchApiKeys();
      showSuccess('API-Key erfolgreich erstellt');
      
      // Reset form
      setNewKeyData({
        name: '',
        permissions: ['articles:read', 'articles:export'],
        expiresInDays: 365,
        allowedIPs: [],
        rateLimit: {
          requestsPerMinute: 100,
          requestsPerHour: 2000,
          requestsPerDay: 20000
        }
      });
    } catch (error) {
      showError('Fehler beim Erstellen des API-Keys');
      console.error('Error creating API key:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleApiKey = async (keyId: string, isActive: boolean) => {
    try {
      const action = isActive ? 'deactivate' : 'activate';
      await api.put(`/api-keys/${keyId}/${action}`);

      fetchApiKeys();
      showSuccess(`API-Key erfolgreich ${isActive ? 'deaktiviert' : 'aktiviert'}`);
    } catch (error) {
      showError(`Fehler beim ${isActive ? 'Deaktivieren' : 'Aktivieren'} des API-Keys`);
      console.error('Error toggling API key:', error);
    }
  };

  const deleteApiKey = async (keyId: string, name: string) => {
    if (!window.confirm(`Sind Sie sicher, dass Sie den API-Key "${name}" permanent löschen möchten?\n\nDiese Aktion kann nicht rückgängig gemacht werden!`)) {
      return;
    }

    try {
      await api.delete(`/api-keys/${keyId}`);
      fetchApiKeys();
      showSuccess('API-Key erfolgreich gelöscht');
    } catch (error) {
      showError('Fehler beim Löschen des API-Keys');
      console.error('Error deleting API key:', error);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showSuccess('In Zwischenablage kopiert');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Nie';
    return new Date(dateString).toLocaleString('de-DE');
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">API-Key Management</h3>
          <p className="text-gray-600 text-sm mt-1">
            Verwalten Sie API-Keys für PIM-Integration und externe Systeme
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          Neuen API-Key erstellen
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {apiKeys.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Keine API-Keys vorhanden. Erstellen Sie Ihren ersten API-Key.
            </div>
          ) : (
            apiKeys.map((apiKey) => (
              <div key={apiKey.keyId} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-medium text-gray-900">{apiKey.name}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        apiKey.isActive && apiKey.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {apiKey.isActive && apiKey.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div><strong>Key ID:</strong> {apiKey.keyId}</div>
                      <div><strong>Berechtigungen:</strong> {apiKey.permissions.join(', ')}</div>
                      <div><strong>Erstellt:</strong> {formatDate(apiKey.createdAt)}</div>
                      <div><strong>Letzter Zugriff:</strong> {formatDate(apiKey.lastUsedAt)}</div>
                      {apiKey.expiresAt && (
                        <div><strong>Läuft ab:</strong> {formatDate(apiKey.expiresAt)}</div>
                      )}
                      <div><strong>Rate Limit:</strong> {apiKey.rateLimit.requestsPerMinute}/min, {apiKey.rateLimit.requestsPerHour}/h, {apiKey.rateLimit.requestsPerDay}/Tag</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleApiKey(apiKey.keyId, apiKey.isActive)}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        apiKey.isActive
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {apiKey.isActive ? 'Deaktivieren' : 'Aktivieren'}
                    </button>
                    <button
                      onClick={() => deleteApiKey(apiKey.keyId, apiKey.name)}
                      className="px-3 py-1 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                      title="API-Key permanent löschen"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full m-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Neuen API-Key erstellen</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={newKeyData.name}
                    onChange={(e) => setNewKeyData({...newKeyData, name: e.target.value})}
                    placeholder="z.B. PIM-System Integration"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Berechtigungen
                  </label>
                  <div className="space-y-2 max-h-32 overflow-y-auto border rounded p-2">
                    {availablePermissions.map((perm) => (
                      <label key={perm.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newKeyData.permissions.includes(perm.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewKeyData({
                                ...newKeyData,
                                permissions: [...newKeyData.permissions, perm.value]
                              });
                            } else {
                              setNewKeyData({
                                ...newKeyData,
                                permissions: newKeyData.permissions.filter(p => p !== perm.value)
                              });
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm">{perm.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gültigkeitsdauer (Tage)
                  </label>
                  <input
                    type="number"
                    value={newKeyData.expiresInDays || ''}
                    onChange={(e) => setNewKeyData({...newKeyData, expiresInDays: parseInt(e.target.value) || undefined})}
                    placeholder="365 (leer = unbegrenzt)"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate Limits
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600">Pro Minute</label>
                      <input
                        type="number"
                        value={newKeyData.rateLimit.requestsPerMinute}
                        onChange={(e) => setNewKeyData({
                          ...newKeyData,
                          rateLimit: {...newKeyData.rateLimit, requestsPerMinute: parseInt(e.target.value)}
                        })}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600">Pro Stunde</label>
                      <input
                        type="number"
                        value={newKeyData.rateLimit.requestsPerHour}
                        onChange={(e) => setNewKeyData({
                          ...newKeyData,
                          rateLimit: {...newKeyData.rateLimit, requestsPerHour: parseInt(e.target.value)}
                        })}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600">Pro Tag</label>
                      <input
                        type="number"
                        value={newKeyData.rateLimit.requestsPerDay}
                        onChange={(e) => setNewKeyData({
                          ...newKeyData,
                          rateLimit: {...newKeyData.rateLimit, requestsPerDay: parseInt(e.target.value)}
                        })}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={createApiKey}
                  disabled={!newKeyData.name || newKeyData.permissions.length === 0 || loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Erstelle...' : 'API-Key erstellen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show Generated API Key Modal */}
      {showKeyModal && createdApiKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-lg w-full m-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-green-700">API-Key erfolgreich erstellt!</h3>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Wichtig!</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>Speichern Sie den API-Key sicher ab. Er wird nicht erneut angezeigt!</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ihr neuer API-Key:
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={createdApiKey}
                    readOnly
                    className="flex-1 bg-gray-50 border border-gray-300 rounded-l-lg px-3 py-2 font-mono text-sm"
                  />
                  <button
                    onClick={() => copyToClipboard(createdApiKey)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-r-lg transition-colors"
                  >
                    Kopieren
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-blue-900 mb-2">So verwenden Sie den API-Key:</h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <p><strong>Authorization Header:</strong></p>
                  <code className="bg-white px-2 py-1 rounded text-xs">
                    Authorization: Bearer {createdApiKey.substring(0, 20)}...
                  </code>
                  <p className="mt-2"><strong>Beispiel-Endpoint:</strong></p>
                  <code className="bg-white px-2 py-1 rounded text-xs">
                    GET /api/pim/articles
                  </code>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowKeyModal(false);
                    setCreatedApiKey(null);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Verstanden
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeyManager;
